import { useMemo, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS } from '@wms/shared';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { ApiError, apiFetch } from '../../lib/api';
import { useCan } from '../../lib/session';
import type { ProductDetail } from '../product/types';
import { StatusBadge } from './orders-list-page';
import { SkeletonBlock, SkeletonRows } from '../skeleton';
import type {
  CancelResponse,
  ConfirmResponse,
  FulfilResponse,
  OrderDetail,
  PickListBatch,
  PickListResponse,
  ReallocateResponse,
} from './types';

export interface PickRow {
  readonly key: string;
  readonly orderLineId: string;
  readonly batchId: string;
  readonly batchNo: string;
  locationId: string;
  qty: string;
}

/** 从拣货推荐初始化可编辑行（拣货人可调整实际取货位置与数量，批次不可改） */
export function rowsFromRecommendation(pickList: PickListResponse): PickRow[] {
  const rows: PickRow[] = [];
  for (const line of pickList.lines) {
    for (const batch of line.batches) {
      for (const rec of batch.recommendation) {
        rows.push({
          key: crypto.randomUUID(),
          orderLineId: line.orderLineId,
          batchId: batch.batchId,
          batchNo: batch.batchNo,
          locationId: rec.locationId,
          qty: rec.qtyBase,
        });
      }
    }
  }
  return rows;
}

function ErrorLine({ error }: { readonly error: unknown }): React.JSX.Element | null {
  const { t } = useTranslation();
  if (!(error instanceof ApiError)) return null;
  return (
    <p className="field-error">
      {t(`errors:${error.code}`, { defaultValue: error.code, ...error.params })}
    </p>
  );
}

/** 订单详情页（S12 整页 + F6 全流程：确认 / 取消 / 履行拣货 / 再分配）。 */
export function OrderDetailPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const orderId = id!;
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  // 每个动作对应后端实际校验的权限码（与 orders.controller.ts 的
  // RequirePermission 逐一对应）——不是同一个权限，起草者未必能确认/取消。
  const canConfirm = useCan(PERMISSIONS.ORDER_CONFIRM);
  const canCancel = useCan(PERMISSIONS.ORDER_CANCEL);
  const canFulfil = useCan(PERMISSIONS.INVENTORY_OUTBOUND);
  const canReallocate = useCan(PERMISSIONS.RESERVATION_RELEASE);
  const fmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' });
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [shipMode, setShipMode] = useState(false);
  const [pickRows, setPickRows] = useState<PickRow[]>([]);

  const { data: order, isPending } = useQuery({
    queryKey: ['orders', 'detail', orderId],
    queryFn: () => apiFetch<OrderDetail>(`/orders/${orderId}`),
  });

  const showPickList = order?.status === 'CONFIRMED' || order?.status === 'PARTIALLY_FULFILLED';
  const { data: pickList } = useQuery({
    queryKey: ['orders', 'pick-list', orderId],
    queryFn: () => apiFetch<PickListResponse>(`/orders/${orderId}/pick-list`),
    enabled: showPickList,
  });

  // 履行数量必须以**基本单位**提交（拣货推荐/清单的 qtyBase 就是基本单位量）——
  // 订单行原始录入单位可能带非 1 的换算因子，绝不能拿来复用，否则数量会被
  // 后端按错误的换算因子重新解释。这里按产品取真正的 baseUnitId。
  const productIds = useMemo(
    () => [...new Set((order?.lines ?? []).map((l) => l.productId))],
    [order],
  );
  const productQueries = useQueries({
    queries: productIds.map((productId) => ({
      queryKey: ['orders', 'product', productId],
      queryFn: () => apiFetch<ProductDetail>(`/products/${productId}`),
    })),
  });
  const baseUnitByLine = useMemo(() => {
    const byProduct = new Map<string, string>();
    productIds.forEach((productId, i) => {
      const unitId = productQueries[i]?.data?.baseUnitId;
      if (unitId) byProduct.set(productId, unitId);
    });
    const byLine = new Map<string, string>();
    for (const line of order?.lines ?? []) {
      const unitId = byProduct.get(line.productId);
      if (unitId) byLine.set(line.id, unitId);
    }
    return byLine;
  }, [order, productIds, productQueries]);

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['orders'] });
  };

  const confirmMutation = useMutation({
    mutationFn: () =>
      apiFetch<ConfirmResponse>(`/orders/${orderId}/confirm`, {
        method: 'POST',
        headers: { 'idempotency-key': crypto.randomUUID() },
        body: {},
      }),
    onSuccess: invalidate,
  });

  const cancelMutation = useMutation({
    mutationFn: () =>
      apiFetch<CancelResponse>(`/orders/${orderId}/cancel`, {
        method: 'POST',
        headers: { 'idempotency-key': crypto.randomUUID() },
        body: {},
      }),
    onSuccess: () => {
      setConfirmCancel(false);
      invalidate();
    },
  });

  const reallocateMutation = useMutation({
    mutationFn: (orderLineId: string) =>
      apiFetch<ReallocateResponse>(`/orders/${orderId}/reallocate`, {
        method: 'POST',
        headers: { 'idempotency-key': crypto.randomUUID() },
        body: { orderLineId },
      }),
    onSuccess: invalidate,
  });

  const fulfilMutation = useMutation({
    mutationFn: () =>
      apiFetch<FulfilResponse>(`/orders/${orderId}/fulfil`, {
        method: 'POST',
        headers: { 'idempotency-key': crypto.randomUUID() },
        body: {
          picks: pickRows
            .filter((r) => r.qty.trim() && Number(r.qty) > 0)
            .map((r) => ({
              orderLineId: r.orderLineId,
              batchId: r.batchId,
              locationId: r.locationId,
              quantity: { value: r.qty.trim(), unitId: baseUnitByLine.get(r.orderLineId) ?? '' },
            })),
        },
      }),
    onSuccess: () => {
      setShipMode(false);
      setPickRows([]);
      invalidate();
    },
  });

  const batchByKey = useMemo(() => {
    const map = new Map<string, PickListBatch>();
    for (const line of pickList?.lines ?? []) {
      for (const batch of line.batches) map.set(`${line.orderLineId}:${batch.batchId}`, batch);
    }
    return map;
  }, [pickList]);

  const startShipping = (): void => {
    if (!pickList) return;
    setPickRows(rowsFromRecommendation(pickList));
    setShipMode(true);
  };

  if (isPending || !order)
    return (
      <div style={{ maxWidth: '960px' }}>
        <SkeletonBlock height={64} />
        <SkeletonRows rows={5} />
      </div>
    );

  return (
    <div style={{ maxWidth: '960px' }}>
      <Link to="/orders" className="field-hint">
        {t('orders.backToList')}
      </Link>
      <div className="row row--between" style={{ marginTop: 'var(--space-2)' }}>
        <h1 style={{ fontSize: 'var(--text-2xl)' }}>{order.docNo}</h1>
        <StatusBadge status={order.status} />
      </div>

      <div className="row" style={{ gap: 'var(--space-4)', margin: 'var(--space-3) 0' }}>
        <span>
          <strong>{t('orders.customerRefLabel')}:</strong> {order.customerRefText ?? '—'}
        </span>
        <span>
          <strong>{t('orders.requiredDateLabel')}:</strong>{' '}
          {order.requiredDate ? fmt.format(new Date(order.requiredDate)) : '—'}
        </span>
      </div>
      {order.notes && <p className="field-hint">{order.notes}</p>}

      <div className="card" style={{ padding: 'var(--space-3)', marginTop: 'var(--space-4)' }}>
        {order.lines.map((line) => (
          <div
            key={line.id}
            style={{
              padding: 'var(--space-2) 0',
              borderBottom: '1px solid var(--color-border)',
            }}
          >
            <div className="row row--between">
              <strong>{line.productName}</strong>
              <span className="field-hint">
                {t('orders.lineQuantities', {
                  requested: line.qtyBaseRequested,
                  reserved: line.qtyBaseReserved,
                  fulfilled: line.qtyBaseFulfilled,
                })}
              </span>
            </div>
            {line.allocations.map((alloc) => {
              const pickBatch = batchByKey.get(`${line.id}:${alloc.batchId}`);
              const shortfall = pickBatch ? BigInt(pickBatch.shortfallBase) : 0n;
              return (
                <div key={alloc.batchId} style={{ marginLeft: 'var(--space-4)' }}>
                  <span className="field-hint">
                    {t('orders.allocationLine', {
                      batch: alloc.batchNo,
                      qty: alloc.qtyBase,
                    })}
                  </span>
                  {pickBatch && pickBatch.distribution.length > 0 && (
                    <div style={{ marginLeft: 'var(--space-4)' }}>
                      {pickBatch.distribution.map((d) => (
                        <div key={d.locationId} className="field-hint">
                          {t('orders.distributionLine', {
                            location: d.label,
                            qty: d.qtyOnHandBase,
                          })}
                        </div>
                      ))}
                    </div>
                  )}
                  {shortfall > 0n && (
                    <div className="row" style={{ gap: 'var(--space-2)', alignItems: 'center' }}>
                      <span className="badge badge--danger">
                        {t('orders.reservationAtRiskBadge', { qty: shortfall.toString() })}
                      </span>
                      {canReallocate && (
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          disabled={reallocateMutation.isPending}
                          onClick={() => reallocateMutation.mutate(line.id)}
                        >
                          {t('orders.reallocateButton')}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <ErrorLine error={confirmMutation.error} />
      <ErrorLine error={cancelMutation.error} />
      <ErrorLine error={reallocateMutation.error} />
      {reallocateMutation.isSuccess && (
        <p className="badge badge--success">
          {reallocateMutation.data.moved.length > 0
            ? t('orders.reallocateSuccess', { count: reallocateMutation.data.moved.length })
            : t('orders.reallocateNoop')}
        </p>
      )}

      <div className="row" style={{ gap: 'var(--space-2)', marginTop: 'var(--space-4)' }}>
        {order.status === 'DRAFT' && (
          <>
            {canConfirm && (
              <button
                className="btn btn--primary"
                disabled={confirmMutation.isPending}
                onClick={() => confirmMutation.mutate()}
              >
                {confirmMutation.isPending ? t('app.loading') : t('orders.confirmButton')}
              </button>
            )}
            {canCancel && (
              <button className="btn btn--ghost" onClick={() => setConfirmCancel(true)}>
                {t('orders.cancelButton')}
              </button>
            )}
          </>
        )}
        {(order.status === 'CONFIRMED' || order.status === 'PARTIALLY_FULFILLED') && (
          <>
            {canFulfil && (
              <button
                className="btn btn--primary"
                disabled={!pickList || pickList.lines.length === 0}
                onClick={startShipping}
              >
                {t('orders.fulfilButton')}
              </button>
            )}
            {canCancel && (
              <button className="btn btn--ghost" onClick={() => setConfirmCancel(true)}>
                {t('orders.cancelButton')}
              </button>
            )}
          </>
        )}
      </div>

      {shipMode && (
        <div className="modal-overlay" role="presentation" onClick={() => setShipMode(false)}>
          <div
            className="modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <h2>{t('orders.pickListTitle')}</h2>
            {pickRows.map((row) => {
              const batch = batchByKey.get(`${row.orderLineId}:${row.batchId}`);
              return (
                <div
                  key={row.key}
                  className="row"
                  style={{ gap: 'var(--space-2)', alignItems: 'flex-end' }}
                >
                  <span style={{ minWidth: '140px' }}>{row.batchNo}</span>
                  <label className="field" style={{ maxWidth: '220px' }}>
                    <span>{t('orders.pickLocation')}</span>
                    <select
                      value={row.locationId}
                      onChange={(e) =>
                        setPickRows((rs) =>
                          rs.map((r) =>
                            r.key === row.key ? { ...r, locationId: e.target.value } : r,
                          ),
                        )
                      }
                    >
                      {(batch?.distribution ?? []).map((d) => (
                        <option key={d.locationId} value={d.locationId}>
                          {d.label} ({d.qtyOnHandBase})
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="field" style={{ maxWidth: '120px' }}>
                    <span>{t('inbound.stepQuantity')}</span>
                    <input
                      inputMode="decimal"
                      value={row.qty}
                      onChange={(e) =>
                        setPickRows((rs) =>
                          rs.map((r) => (r.key === row.key ? { ...r, qty: e.target.value } : r)),
                        )
                      }
                    />
                  </label>
                </div>
              );
            })}
            <ErrorLine error={fulfilMutation.error} />
            <div className="modal__actions">
              <button
                type="button"
                className="btn btn--ghost"
                disabled={fulfilMutation.isPending}
                onClick={() => setShipMode(false)}
              >
                {t('app.cancel')}
              </button>
              <button
                type="button"
                className="btn btn--primary"
                disabled={fulfilMutation.isPending || pickRows.length === 0}
                onClick={() => fulfilMutation.mutate()}
              >
                {fulfilMutation.isPending ? t('app.loading') : t('orders.pickSubmit')}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmCancel && (
        <div className="modal-overlay" role="presentation" onClick={() => setConfirmCancel(false)}>
          <div
            className="modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <h2>{t('orders.cancelConfirmTitle')}</h2>
            <p>{t('orders.cancelConfirmBody', { doc: order.docNo })}</p>
            <ErrorLine error={cancelMutation.error} />
            <div className="modal__actions">
              <button
                type="button"
                className="btn btn--ghost"
                disabled={cancelMutation.isPending}
                onClick={() => setConfirmCancel(false)}
              >
                {t('app.cancel')}
              </button>
              <button
                type="button"
                className="btn btn--primary"
                disabled={cancelMutation.isPending}
                onClick={() => cancelMutation.mutate()}
              >
                {cancelMutation.isPending ? t('app.loading') : t('orders.cancelConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
